import { createContext, useContext, type ReactNode } from 'react';
import { Outlet, useLocation } from 'react-router';
import { StateScreen } from '@sauda/ui/kit';
import { useWindowClass } from './media.js';

/** Which pane a screen is drawn in: the list, the detail beside it, or no panes at all. */
export type PaneKind = 'list' | 'detail' | null;

const PaneContext = createContext<PaneKind>(null);

export function usePane(): PaneKind {
  return useContext(PaneContext);
}

/** The pane's own scroll container; the screens inside lay themselves out for the pane. */
export function Pane({
  kind,
  label,
  children,
}: {
  kind: 'list' | 'detail';
  label: string;
  children: ReactNode;
}) {
  return (
    <section className={`pane pane--${kind}`} aria-label={label}>
      <PaneContext.Provider value={kind}>{children}</PaneContext.Provider>
    </section>
  );
}

/**
 * List and detail. From 840 px (expanded) they sit side by side: the list stays put and the
 * detail is whatever the nested route shows, or an empty state. Below that there is one pane at
 * a time: the list, or the detail when a nested route is open. It follows the window live.
 */
export function TwoPane({
  base,
  list,
  listLabel,
  detailLabel,
  emptyTitle,
}: {
  /** The list's path, for example `/properties`. A deeper path means a detail is open. */
  base: string;
  list: ReactNode;
  listLabel: string;
  detailLabel: string;
  emptyTitle: string;
}) {
  const windowClass = useWindowClass();
  const { pathname } = useLocation();
  const detailOpen = pathname.replace(/\/+$/, '') !== base;
  if (windowClass === 'compact' || windowClass === 'medium') {
    return detailOpen ? <Outlet /> : <>{list}</>;
  }
  return (
    <div className="panes">
      <Pane kind="list" label={listLabel}>
        {list}
      </Pane>
      <Pane kind="detail" label={detailLabel}>
        {detailOpen ? <Outlet /> : <StateScreen kind="empty" title={emptyTitle} />}
      </Pane>
    </div>
  );
}

/** A list pane plus a detail pane that is state, not a route (renewals, team members). */
export function StatePanes({
  list,
  detail,
  listLabel,
  detailLabel,
  emptyTitle,
}: {
  list: ReactNode;
  /** The open detail, or null when none is chosen. */
  detail: ReactNode | null;
  listLabel: string;
  detailLabel: string;
  emptyTitle: string;
}) {
  return (
    <div className="panes">
      <Pane kind="list" label={listLabel}>
        {list}
      </Pane>
      <Pane kind="detail" label={detailLabel}>
        {detail ?? <StateScreen kind="empty" title={emptyTitle} />}
      </Pane>
    </div>
  );
}
